import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { api } from '../../api';
import { router } from '../../router';
import { showToast } from '../../components/ui/toast';
import { money } from '../../format';
import { renderMarkdown } from '../../markdown';
import { t } from '../../i18n';
import './portal-event-gallery';

interface Fee { id: string; name: string; amount: number; currency: string; }
interface Participant { status: string; event_fee_id: string | null; fee_payment_id: string | null; agreed_at: string | null; }
interface EventDetail {
  id: string; name: string; type_name: string | null; description: string | null;
  start_date: string | null; end_date: string | null; status: string;
  details_md: string | null; details_filename: string | null;
}

/** Student portal — a single event: read the details document, accept the conditions,
 *  choose a pricing level and register interest. Once the school confirms, the fee
 *  appears under Payments. */
@customElement('cadence-portal-event-detail')
export class CadencePortalEventDetail extends LitElement {
  @property() eventId = '';
  @state() private loading = true;
  @state() private event: EventDetail | null = null;
  @state() private fees: Fee[] = [];
  @state() private participant: Participant | null = null;

  // Registration form state
  @state() private chosenFee = '';
  @state() private agreed = false;
  @state() private submitting = false;

  static styles = css`
    :host { display: block; }
    .back { color: var(--color-primary); text-decoration: none; font-size: 0.88rem; }
    h1 { margin: var(--space-3) 0 4px; font-size: 1.5rem; }
    .sub { color: var(--color-text-secondary); margin: 0 0 var(--space-4); }
    .card { border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg-primary); padding: var(--space-4); margin-bottom: var(--space-4); }
    .card h2 { font-size: 1.05rem; margin: 0 0 var(--space-3); }
    .status { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
    .b { display: inline-block; padding: 3px 12px; border-radius: 999px; font-size: 0.8rem; font-weight: 600; }
    .b-interested { background: #fef9c3; color: #854d0e; }
    .b-confirmed { background: #dcfce7; color: #166534; }
    .b-denied { background: #fee2e2; color: #991b1b; }
    .fees { display: flex; flex-direction: column; gap: 8px; margin: var(--space-3) 0; }
    .fee { display: flex; align-items: center; gap: 10px; padding: 10px 12px; border: 1px solid var(--color-border); border-radius: var(--radius-md); cursor: pointer; }
    .fee.sel { border-color: var(--color-primary); box-shadow: 0 0 0 2px color-mix(in srgb, var(--color-primary) 25%, transparent); }
    .fee input { accent-color: var(--color-primary); width: 16px; height: 16px; }
    .fee .nm { font-weight: 600; flex: 1; }
    .fee .amt { font-weight: 700; }
    .agree { margin: var(--space-3) 0; }
    .muted { color: var(--color-text-secondary); font-size: 0.85rem; }
    .actions { display: flex; gap: 10px; align-items: center; margin-top: var(--space-3); flex-wrap: wrap; }
    .md :first-child { margin-top: 0; }
    .md h1, .md h2, .md h3 { font-size: 1.15rem; margin: 16px 0 8px; }
    .md p, .md ul, .md ol { margin: 10px 0; line-height: 1.6; }
    .md li { margin: 4px 0; }
    .md pre { background: var(--color-bg-secondary); padding: 12px; border-radius: 8px; overflow: auto; }
    .md code { background: var(--color-bg-secondary); padding: 1px 5px; border-radius: 4px; }
    .md pre code { padding: 0; background: none; }
    .md blockquote { margin: 10px 0; padding: 4px 14px; border-left: 3px solid var(--color-primary); color: var(--color-text-secondary); }
    .md a { color: var(--color-primary); }
    .md hr { border: none; border-top: 1px solid var(--color-border); margin: 16px 0; }
    .md table { border-collapse: collapse; width: 100%; margin: 12px 0; font-size: 0.95rem; }
    .md th, .md td { border: 1px solid var(--color-border); padding: 8px 12px; text-align: left; }
    .md th { background: var(--color-bg-secondary); font-weight: 600; }
    .md-card { border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg-primary); margin-bottom: var(--space-4); }
    .md-card > summary { cursor: pointer; padding: var(--space-4); font-weight: 700; font-size: 1.05rem; list-style: none; display: flex; align-items: center; gap: 10px; user-select: none; }
    .md-card > summary::-webkit-details-marker { display: none; }
    .md-card > summary::before { content: '▸'; color: var(--color-text-secondary); font-size: 0.9rem; transition: transform 0.15s; }
    .md-card[open] > summary::before { transform: rotate(90deg); }
    .md-card > summary .muted { font-weight: 400; }
    .md-card .md { padding: 0 var(--space-4) var(--space-4); }
  `;

  connectedCallback(): void { super.connectedCallback(); this.load(); }
  updated(c: Map<string, unknown>) { if (c.has('eventId') && this.eventId) this.load(); }

  private async load() {
    if (!this.eventId) return;
    this.loading = true;
    try {
      const res = await api.get<{ event: EventDetail; fees: Fee[]; participant: Participant | null }>(`/portal/events/${this.eventId}`);
      this.event = res.event; this.fees = res.fees; this.participant = res.participant;
      this.chosenFee = res.participant?.event_fee_id ?? (res.fees.length === 1 ? res.fees[0].id : '');
    } catch (e) { showToast((e as Error).message, 'error'); router.navigate('/portal/events'); }
    finally { this.loading = false; }
  }

  private get needsFee() { return this.fees.length > 0; }
  private get canSubmit() { return this.agreed && (!this.needsFee || !!this.chosenFee) && !this.submitting; }

  private async register() {
    if (!this.canSubmit) return;
    this.submitting = true;
    try {
      await api.post(`/portal/events/${this.eventId}/interest`, { agreed: true, event_fee_id: this.chosenFee || undefined });
      showToast(t('portal.toast.interestRegistered'), 'success');
      this.agreed = false;
      await this.load();
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.submitting = false; }
  }

  private async withdraw() {
    if (!confirm(t('portal.eventDetail.withdrawConfirm'))) return;
    try { await api.del(`/portal/events/${this.eventId}/interest`); showToast(t('portal.toast.interestWithdrawn'), 'info'); await this.load(); }
    catch (e) { showToast((e as Error).message, 'error'); }
  }

  private dates(e: EventDetail) {
    if (!e.start_date) return t('portal.eventDetail.dateTbc');
    return e.end_date && e.end_date !== e.start_date ? `${e.start_date} → ${e.end_date}` : e.start_date;
  }
  private feeLabel(id: string | null) { const f = this.fees.find((x) => x.id === id); return f ? `${f.name} — ${money(f.amount, f.currency)}` : null; }

  render() {
    if (this.loading || !this.event) return html`<cadence-loading></cadence-loading>`;
    const e = this.event;
    return html`
      <a class="back" href="/portal/events" data-link>← ${t('portal.eventDetail.allEvents')}</a>
      <h1>${e.name}</h1>
      <p class="sub">${e.type_name ?? t('portal.events.eventFallback')} · ${this.dates(e)}</p>

      ${e.description ? html`<div class="card"><p style="white-space:pre-wrap;margin:0">${e.description}</p></div>` : ''}

      ${this.renderParticipation()}

      ${e.details_md ? html`<details class="md-card" open>
        <summary>${t('portal.eventDetail.eventDetails')}${e.details_filename ? html` <span class="muted">· ${e.details_filename}</span>` : ''}</summary>
        <div class="md">${unsafeHTML(renderMarkdown(e.details_md))}</div>
      </details>` : ''}

      <div class="card">
        <h2>${t('portal.eventDetail.photosVideos')}</h2>
        <cadence-portal-event-gallery .eventId=${this.eventId}></cadence-portal-event-gallery>
      </div>
    `;
  }

  private renderParticipation() {
    const p = this.participant;
    const closed = this.event!.status !== 'published';

    // Already registered.
    if (p) {
      if (p.status === 'confirmed') {
        return html`<div class="card">
          <div class="status"><span class="b b-confirmed">${t('portal.eventDetail.confirmedHeading')}</span></div>
          ${p.fee_payment_id
            ? html`<p class="muted" style="margin:12px 0 0">${t('portal.eventDetail.feeAdded')}</p>
                <div class="actions"><cadence-button @click=${() => router.navigate('/portal/payments')}>${t('portal.eventDetail.goToPayments')}</cadence-button></div>`
            : html`<p class="muted" style="margin:12px 0 0">${t('portal.eventDetail.feeTbd')}</p>`}
        </div>`;
      }
      if (p.status === 'denied') {
        return html`<div class="card"><div class="status"><span class="b b-denied">${t('portal.events.notApproved')}</span></div>
          <p class="muted" style="margin:12px 0 0">${t('portal.eventDetail.deniedNote')}</p></div>`;
      }
      // interested
      const feeSuffix = this.feeLabel(p.event_fee_id) ? ` (${this.feeLabel(p.event_fee_id)})` : '';
      return html`<div class="card">
        <div class="status"><span class="b b-interested">${t('portal.events.pendingApproval')}</span></div>
        <p class="muted" style="margin:12px 0 0">${t('portal.eventDetail.interestedNote', { fee: feeSuffix })}</p>
        <div class="actions"><cadence-button variant="secondary" @click=${() => this.withdraw()}>${t('portal.eventDetail.withdrawInterest')}</cadence-button></div>
      </div>`;
    }

    // Not registered yet.
    if (closed) {
      return html`<div class="card"><p class="muted" style="margin:0">${t('portal.eventDetail.registrationClosed')}</p></div>`;
    }

    return html`<div class="card">
      <h2>${t('portal.eventDetail.registerHeading')}</h2>
      ${this.needsFee ? html`
        <p class="muted">${t('portal.eventDetail.chooseOption')}</p>
        <div class="fees">
          ${this.fees.map((f) => html`
            <label class="fee ${this.chosenFee === f.id ? 'sel' : ''}">
              <input type="radio" name="fee" .checked=${this.chosenFee === f.id} @change=${() => (this.chosenFee = f.id)} />
              <span class="nm">${f.name}</span><span class="amt">${money(f.amount, f.currency)}</span>
            </label>`)}
        </div>` : ''}

      <div class="agree">
        <cadence-checkbox ?checked=${this.agreed} @change=${(e: CustomEvent) => (this.agreed = e.detail)}
          label=${this.event!.details_md ? t('portal.eventDetail.agreeWithDetails') : t('portal.eventDetail.agreeNoDetails')}></cadence-checkbox>
      </div>

      <div class="actions">
        <cadence-button ?loading=${this.submitting} ?disabled=${!this.canSubmit} @click=${() => this.register()}>${t('portal.eventDetail.registerInterest')}</cadence-button>
        ${this.needsFee && !this.chosenFee ? html`<span class="muted">${t('portal.eventDetail.selectOption')}</span>` : ''}
      </div>
    </div>`;
  }
}
